import {useState} from "react";
import {App as AntdApp, Table, Tag, Button as AntdButton, Modal, Spin, Input} from "antd";
import {FontAwesomeIcon} from "~/components";
import {useDebounce} from "~/hooks";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAiHistoryQuery,
	useGetAiHistoryDetailQuery,
	useDeleteAiHistoryMutation,
} from "~/reduxs/api/aiApiSlice";
import AiResult from "./AiResult";

/**
 * Bảng lịch sử AI dùng chung: có ô tìm kiếm + xem chi tiết (AiResult) + xoá. Nếu truyền
 * `projectId` thì chỉ liệt kê lịch sử của dự án đó (BE lọc `project_id`) — dùng cho tab
 * "Lịch sử" trong màn AI của dự án. Không truyền → toàn bộ lịch sử của user (màn độc lập).
 */
function AiHistoryList({projectId}) {

	const {modal, notification} = AntdApp.useApp();

	const [keyword, setKeyword] = useState('');
	const search = useDebounce(keyword.trim());

	const params = {
		...(projectId ? {project_id: projectId} : {}),
		...(search ? {keyword: search} : {}),
	};
	const {data: rows = [], isLoading, isFetching} = useGetAiHistoryQuery(params);
	const [deleteAiHistory] = useDeleteAiHistoryMutation();

	const [viewId, setViewId] = useState(null);
	const {data: detail, isFetching: loadingDetail} = useGetAiHistoryDetailQuery(viewId, {skip: !viewId});

	const confirmDelete = (id) => {
		modal.confirm({
			title: 'Xoá bản ghi này?',
			okText: 'Xoá',
			okButtonProps: {danger: true},
			cancelText: 'Huỷ',
			onOk: async () => {
				try {
					await deleteAiHistory(id).unwrap();
					notification.success({message: 'Đã xoá'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
				}
			},
		});
	};

	const columns = [
		{title: 'Tác vụ', dataIndex: 'task_label', render: (v) => <Tag color="geekblue">{v}</Tag>},
		{title: 'Nội dung', dataIndex: 'preview', ellipsis: true, render: (v) => v || <i style={{color: '#aaa'}}>—</i>},
		{title: 'Provider', dataIndex: 'provider', width: 110, responsive: ['md'], render: (v, r) => <span style={{fontSize: 12}}>{v}<br /><span style={{color: '#888'}}>{r.model}</span></span>},
		{title: 'Token', dataIndex: 'tokens_out', width: 90, responsive: ['md'], render: (v, r) => <span style={{fontSize: 12}}>{r.tokens_in}+{v}</span>},
		{title: 'Thời gian', dataIndex: 'created', width: 160, responsive: ['md']},
		{
			title: '',
			width: 120,
			render: (_, r) => (
				<span style={{display: 'flex', gap: 4}}>
					<AntdButton size="small" onClick={() => setViewId(r.id)}
						icon={<FontAwesomeIcon icon="fa-light fa-eye" />}>Xem</AntdButton>
					<AntdButton size="small" danger onClick={() => confirmDelete(r.id)}
						icon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</span>
			),
		},
	];

	return (
		<div>
			<Input
				allowClear
				prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
				placeholder="Tìm trong nội dung đã nhập / kết quả…"
				value={keyword}
				onChange={(e) => setKeyword(e.target.value)}
				style={{maxWidth: 340, marginBottom: 14}}
			/>

			<Table
				rowKey="id"
				loading={isLoading || isFetching}
				columns={columns}
				dataSource={rows}
				locale={{emptyText: search ? `Không có bản ghi nào khớp “${search}”` : 'Chưa có lịch sử AI'}}
				pagination={{pageSize: 20, hideOnSinglePage: true}}
				size="middle"
				scroll={{x: 'max-content'}}
			/>

			<Modal
				open={!!viewId}
				onCancel={() => setViewId(null)}
				footer={null}
				width={720}
				title="Kết quả AI"
			>
				{loadingDetail && <div style={{textAlign: 'center', padding: 40}}><Spin /></div>}
				{!loadingDetail && detail && <AiResult result={detail} />}
			</Modal>
		</div>
	);
}

export default AiHistoryList;
